import type { DocTxOps } from '@djgg/core';
import { creationDoc, versionDoc } from '../paths.js';
import type { BuildStatus, Creation, CreationVersion, VersionGates } from '../types.js';

/** Version states a build passes through before it ends. */
const IN_FLIGHT_BUILD_STATUSES: ReadonlySet<BuildStatus> = new Set(['queued', 'generating', 'gating', 'publishing']);

export function isInFlightBuildStatus(status: BuildStatus | undefined): boolean {
  return !!status && IN_FLIGHT_BUILD_STATUSES.has(status);
}

export interface HealTerminalBuildInput {
  creationId: string;
  version: number;
  /** The rows as read inside the caller's transaction. */
  creation: Creation | null;
  versionRow: CreationVersion | null;
  /** Plain-English reason recorded on the version's gate notes. */
  notes: string;
  ts: unknown;
  /** Extra gate fields recorded with the failure (e.g. who terminated it). */
  gates?: Partial<VersionGates>;
  /** Extra version fields the terminal path records (trace, cost, …). */
  versionPatch?: Record<string, unknown>;
}

/**
 * THE heal every terminal build path runs once its job has ended without a
 * publication: an in-flight version becomes `failed`, and a creation head that
 * still points at that version leaves `building` — `live` while an earlier
 * version is playable, else `failed`. Runs inside the caller's transaction so
 * the job, version and head end together. A head that already moved to a newer
 * version, and a version that already ended, are left alone.
 *
 * Every terminal-job writer (cancel, admin force-fail, reaper, worker failure,
 * generic retirement, verified-quality and Composition V2 lifecycles, and the
 * reaper's stranded-head sweep) calls this; none writes the heal fields itself.
 */
export async function healTerminalBuild(
  tx: DocTxOps,
  input: HealTerminalBuildInput,
): Promise<{ versionHealed: boolean; headHealed: boolean }> {
  const { creationId, version, creation, versionRow, ts } = input;
  const versionHealed = !!versionRow && isInFlightBuildStatus(versionRow.buildStatus);
  if (versionHealed) {
    await tx.update(versionDoc(creationId, version), {
      ...input.versionPatch,
      buildStatus: 'failed',
      gates: { ...versionRow.gates, ...input.gates, notes: input.notes },
      finishedAt: ts,
    });
  }
  const headHealed = !!creation && creation.latestVersion === version && creation.buildState === 'building';
  if (headHealed) {
    const playable = (creation.activeVersion ?? 0) > 0 && !!creation.activeBundleUrl;
    await tx.update(creationDoc(creationId), { buildState: 'idle', status: playable ? 'live' : 'failed', updatedAt: ts });
  }
  return { versionHealed, headHealed };
}
